import React from 'react';
import { ProductItemWithDetails } from 'utils/grocyApi.ts';
import { useQueue } from 'contexts/QueueContext.tsx';
import { useStockQuery } from 'hooks/useQueries.ts';
import ProductImage from '../purchase/ProductImage.tsx';
import './ScannedItemDisplay.css';

export interface ScannedItemDisplayProps {
  productItem: ProductItemWithDetails;
  barcode: string;
  status?: 'ready' | 'no-stock';
  onClear: () => void;
  className?: string;
}

const ScannedItemDisplay: React.FC<ScannedItemDisplayProps> = ({
  productItem,
  barcode,
  status = 'ready',
  onClear,
  className = ""
}) => {
  const { addConsumeOperation } = useQueue();
  const stockQuery = useStockQuery();

  // Get current stock amount from live cache data instead of snapshot
  const getCurrentStockAmount = () => {
    if (!stockQuery.data) return productItem.stock.amount;
    
    const currentStock = stockQuery.data.find((stock: { product_id: number; amount: number }) => 
      stock.product_id === productItem.product.id
    );
    
    return currentStock ? currentStock.amount : 0;
  };

  const currentStockAmount = getCurrentStockAmount();
  const isOutOfStock = status === 'no-stock' || currentStockAmount <= 0;

  const handleConsume = () => {
    if (isOutOfStock) {
      return;
    }

    addConsumeOperation(productItem.product.id);
    // Close the display and return to scanning mode
    onClear();
  };

  return (
    <div className={`scanned-item-display scanned-item-display--${isOutOfStock ? 'no-stock' : 'ready'} ${className}`}>
      <div className="scanned-item-display__content">
        <div className="scanned-item-display__status-row">
          <span className={`scanned-item-display__status-badge scanned-item-display__status-badge--${isOutOfStock ? 'warning' : 'success'}`}>
            {isOutOfStock ? 'Recognized • Out of stock' : 'Ready to consume'}
          </span>
        </div>
        <div className="scanned-item-display__header">
          <ProductImage 
            product={productItem.product}
            size="medium"
            alt={productItem.product.name}
          />
          <div className="scanned-item-display__product-info">
            <h3 className="scanned-item-display__product-name">
              {productItem.product.name}
            </h3>
            <p className="scanned-item-display__message">
              {isOutOfStock
                ? 'This barcode matches a product in Grocy, but there is no stock available to consume right now.'
                : 'This item is in stock and ready to be consumed.'}
            </p>
            <div className="scanned-item-display__details">
              <div className="scanned-item-display__location">
                <span className="scanned-item-display__detail-icon" aria-hidden="true">📍</span>
                {productItem.location.name}
              </div>
              <div className={`scanned-item-display__stock ${isOutOfStock ? 'scanned-item-display__stock--empty' : ''}`}>
                <span className="scanned-item-display__detail-icon" aria-hidden="true">📊</span>
                {currentStockAmount} {productItem.quantityUnit.name}
              </div>
            </div>
          </div>
        </div>
        
        <div className="scanned-item-display__actions">
          <button
            type="button"
            onClick={onClear}
            className="scanned-item-display__clear-button"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConsume}
            className="scanned-item-display__consume-button"
            disabled={isOutOfStock}
          >
            {isOutOfStock ? 'Nothing to Consume' : 'Consume 1'}
          </button>
        </div>
        
        <div className="scanned-item-display__barcode">
          Last scan: {barcode}
        </div>
      </div>
    </div>
  );
};

export default ScannedItemDisplay; 
